import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ObjectSchema, object, string } from 'yup';

import { UploadedFile } from 'src/interfaces/FileManager';
import useFileManagerStore from 'src/stores/fileManagerStore';

interface EditMetadataDialogProps {
  open: boolean;
  file: UploadedFile | null;
  onClose: () => void;
}

interface IFormInputs {
  fileName: string;
  altText?: string;
}

const EditMetadataDialog = ({ open, file, onClose }: EditMetadataDialogProps) => {
  const { t } = useTranslation();
  const { updateFileMetadata, loading } = useFileManagerStore();

  const schema: ObjectSchema<IFormInputs> = object().shape({
    fileName: string().required(t('form.fileNameRequired')),
    altText: string(),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<IFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      fileName: '',
      altText: '',
    },
  });

  useEffect(() => {
    if (file) {
      reset({
        fileName: file.fileName,
        altText: file.altText || '',
      });
    }
  }, [file, reset]);

  const onSubmit = async (data: IFormInputs) => {
    if (!file) return;
    await updateFileMetadata(file.id, data.fileName, data.altText);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{t('system.fileManager.editMetadata.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Controller
              name="fileName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('system.fileManager.editMetadata.fileName')}
                  error={!!errors.fileName}
                  helperText={errors.fileName?.message}
                />
              )}
            />
            {file?.uploadEndpoint === 'image' && (
              <Controller
                name="altText"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('system.fileManager.editMetadata.altText')}
                    error={!!errors.altText}
                    helperText={
                      errors.altText?.message || t('system.fileManager.editMetadata.altTextHelper')
                    }
                  />
                )}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('base.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting || loading}>
            {isSubmitting || loading ? <CircularProgress size={24} /> : t('base.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditMetadataDialog;
